import { inject, injectable } from "inversify";
import * as THREE from "three";
import { type IService, Logger } from "@designboticteam/core";
import { SELECTABLE_LAYER } from "../../layers";
import { SceneService } from "../../stage/SceneService";
import { isLine } from "../../utils/isLine";
import { isMesh } from "../../utils/isMesh";
import { isMeshOrLine, type MeshOrLine } from "../../utils/isMeshOrLine";
import { isPolygon } from "../../utils/isPolygon";
import { createStore } from "./store";

const HIGHLIGHTED_OPACITY = 0.5;
const SELECTED_OPACITY = 1;

@injectable()
class MaterialControlService implements IService {
	public readonly serviceName = "🎨 MaterialControlService";
	private logger = new Logger(`🎬 WORKSPACE 3D | ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#0066cc",
	});

	private selectedObjects: Set<MeshOrLine> = new Set();
	private hiddenObjects: Set<MeshOrLine> = new Set();

	private selectionOriginalOpacity: Map<MeshOrLine, number> = new Map();
	private originalOpacity: Map<MeshOrLine, number> = new Map();

	public readonly store = createStore();

	private get isGhostMode() {
		return this.store.getState().isGhostMode;
	}

	constructor(@inject(SceneService) private sceneService: SceneService) {}

	/**
	 * @name initialize
	 */
	public initialize(): void {
		this.logger.log("initialized");
	}

	/**
	 * @name dispose
	 */
	public dispose(): void {
		for (const [object, opacity] of this.selectionOriginalOpacity) {
			this.setOpacity(object, opacity);
		}

		this.selectionOriginalOpacity.clear();

		for (const object of this.hiddenObjects) {
			object.visible = true;
			object.layers.enable(SELECTABLE_LAYER);
			this.setOpacity(object, this.originalOpacity.get(object) ?? 1.0);
		}

		this.hiddenObjects.clear();
		this.originalOpacity.clear();
		this.selectedObjects.clear();

		this.logger.log("disposed");
	}

	/* HIGHLIGHTING ------------------------------------------------------------- */

	/**
	 * @description Apply hover highlight - skip if the object is already selected
	 */
	public highlight(sceneObject: THREE.Object3D): void {
		if (isMeshOrLine(sceneObject) === false) {
			return;
		}

		if (this.selectedObjects.has(sceneObject)) {
			return;
		}

		this.applyMaterial(sceneObject, "highlighted");
	}

	/**
	 * @name unhighlight
	 * @description Remove hover highlight
	 * - skip if the object is selected
	 */
	public unhighlight(sceneObject: THREE.Object3D): void {
		if (isMeshOrLine(sceneObject) === false) {
			return;
		}

		if (this.selectedObjects.has(sceneObject)) {
			return;
		}

		this.restoreMaterial(sceneObject);
	}

	/* *** */

	/* SELECTING ------------------------------------------------------------------ */

	/**
	 * @name selectObject
	 * @description Select the object and apply selection material
	 */
	public select(sceneObject: THREE.Object3D): void {
		if (isMeshOrLine(sceneObject) === false) {
			this.logger.warn("Only Mesh and Line objects can be selected.", { sceneObject });
			return;
		}

		this.selectedObjects.add(sceneObject);
		this.applyMaterial(sceneObject, "selected");
		// this.store.setState({ selectedObjects: new Set(this.selectedObjects) });
	}

	/**
	 * @name deselect
	 * @description Deselect the object and restore original material
	 */
	public deselect(sceneObject: THREE.Object3D): void {
		if (isMeshOrLine(sceneObject) === false) {
			this.logger.warn("Only Mesh and Line objects can be deselected.", { sceneObject });
			return;
		}

		this.selectedObjects.delete(sceneObject);
		this.restoreMaterial(sceneObject);
		// this.store.setState({ selectedObjects: new Set(this.selectedObjects) });
	}

	/**
	 * @name getSelectedObjects
	 * @description Get the currently selected objects as a read-only set
	 */
	public getSelectedObjects(): ReadonlySet<MeshOrLine> {
		return new Set(this.selectedObjects);
	}

	/* *** */

	/* HIDING ------------------------------------------------------------------- */

	/**
	 * @name isHidden
	 */
	public isHidden(sceneObject: MeshOrLine): boolean {
		return this.hiddenObjects.has(sceneObject);
	}

	/**
	 * @name hideObject
	 * @description Hide the object (`.visible` and `.opacity`)
	 * - Preserving its original state for later restoration.
	 * - If ghost mode is enabled, the object will be made semi-transparent instead of fully invisible.
	 * - Hidden objects are excluded from raycasting to prevent interaction.
	 */
	public hideObject(sceneObject: THREE.Object3D): void {
		if (isMeshOrLine(sceneObject) === false) {
			this.logger.warn("Only Mesh and Line objects can be hidden.", { sceneObject });
			return;
		}

		if (this.hiddenObjects.has(sceneObject) === false) {
			this.hiddenObjects.add(sceneObject);
			this.storeOpacity(sceneObject);
		}

		// Deselect hidden objects (selected state makes no sense for invisible objects)
		if (this.selectedObjects.has(sceneObject) === true) {
			this.deselect(sceneObject);
		}

		// In ghost mode hidden objects remain visible as semi-transparent "ghosts" so the user can locate them on the scene and un-hide if needed.
		sceneObject.visible = this.isGhostMode;
		this.setOpacity(sceneObject, this.isGhostMode ? 0.3 : 0);

		// Exclude hidden objects from raycasting so they can't be hovered/selected
		sceneObject.layers.disable(SELECTABLE_LAYER);
	}

	/**
	 * @name showObject
	 * @description Show the object by restoring its visibility and opacity.
	 * - If ghost mode is enabled, the object will be made semi-transparent instead of fully opaque.
	 * - Re-enables raycasting for the object to allow interaction.
	 */
	public showObject(sceneObject: MeshOrLine): void {
		// Scenario: hover object -> highlight applied -> hide via toolbar -> show
		// Without restore, the object would keep the highlight material.
		this.restoreMaterial(sceneObject);

		sceneObject.visible = true;
		const savedOpacity = this.originalOpacity.get(sceneObject) ?? 1.0;
		this.setOpacity(sceneObject, this.isGhostMode ? 0.7 : savedOpacity);

		this.hiddenObjects.delete(sceneObject);

		// Keep original opacity in ghost mode so setGhostMode(false) can restore it.
		// Scenario: ghost ON → hide → show (gets 0.7) → ghost OFF → should restore original, not 1.0
		if (this.isGhostMode === false) {
			this.originalOpacity.delete(sceneObject);
		}

		// Re-enable raycasting for the now-visible object
		sceneObject.layers.enable(SELECTABLE_LAYER);
	}

	/**
	 * @name showAll
	 * @description Show all objects that were previously hidden, restoring their visibility and opacity.
	 */
	public showAll(): void {
		for (const object of [...this.hiddenObjects]) {
			this.showObject(object);
		}
	}

	/**
	 * @name hideSelected
	 * @description Hide all currently selected objects and deselect them.
	 */
	public hideSelected(): void {
		for (const obj of this.selectedObjects) {
			this.deselect(obj);
			this.hideObject(obj);
		}
	}

	/* *** */

	/* GHOST MODE --------------------------------------------------------------- */
	public setGhostMode(isEnabled: boolean): void {
		this.store.setState({ isGhostMode: isEnabled });

		this.sceneService.scene.traverse((object) => {
			if (isMesh(object) === false && isLine(object) === false) {
				return;
			}

			if (object.userData.isPolylineVertexHandle === true) {
				return;
			}

			if (this.hiddenObjects.has(object)) {
				object.visible = isEnabled;
				this.setOpacity(object, isEnabled ? 0.3 : 0);
			} else {
				object.visible = true;

				if (isEnabled) {
					this.storeOpacity(object);
					this.setOpacity(object, 0.7);
				} else {
					this.setOpacity(object, this.originalOpacity.get(object) ?? 1.0);
					this.originalOpacity.delete(object);
				}
			}
		});
	}

	/* *** */

	/**
	 * @description Apply highlight/selection visual by raising the object's opacity
	 * instead of replacing its material — preserves the original color of the shape.
	 */
	private applyMaterial(sceneObject: MeshOrLine, materialType: "selected" | "highlighted"): void {
		if (sceneObject.material instanceof THREE.Material === false) {
			this.logger.warn("Multi-material objects are not supported.", { sceneObject });
			return;
		}

		if (this.selectionOriginalOpacity.has(sceneObject) === false) {
			this.selectionOriginalOpacity.set(sceneObject, sceneObject.material.opacity);
		}

		const targetOpacity = materialType === "selected" ? SELECTED_OPACITY : HIGHLIGHTED_OPACITY;
		this.setOpacity(sceneObject, targetOpacity);

		if (isPolygon(sceneObject)) {
			this.applyMaterial(sceneObject.userData.fill, materialType);
		}
	}

	/**
	 * @description Restore opacity to whatever was stored before highlight/select.
	 */
	private restoreMaterial(sceneObject: MeshOrLine): void {
		const originalOpacity = this.selectionOriginalOpacity.get(sceneObject);

		if (originalOpacity === undefined) {
			return;
		}

		this.setOpacity(sceneObject, originalOpacity);
		this.selectionOriginalOpacity.delete(sceneObject);

		if (isPolygon(sceneObject)) {
			this.restoreMaterial(sceneObject.userData.fill);
		}
	}

	/**
	 * @name storeOpacity
	 */
	private storeOpacity(sceneObject: MeshOrLine): void {
		if (sceneObject.material instanceof THREE.Material === false) {
			this.logger.warn("Multi-material objects are not supported for opacity control.", { sceneObject });
			return;
		}

		if (this.originalOpacity.has(sceneObject) === false) {
			this.originalOpacity.set(sceneObject, sceneObject.material.opacity);
		}
	}

	/**
	 * @name setOpacity
	 */
	private setOpacity(sceneObject: MeshOrLine, opacity: number): void {
		if (sceneObject.material instanceof THREE.Material === false) {
			return;
		}

		sceneObject.material.transparent = opacity < 1;
		sceneObject.material.opacity = opacity;
	}
}

export { MaterialControlService };
